import React, { useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useDashboardUi, statusToSemantic } from '../../config/dashboardUiTheme';
import { cn } from '../../lib/utils';
import { 
  useRiskAssessments, 
  useComplaints,
  useDeviations,
  useApprovalRequests
} from '../../hooks/useEntities';

const RecentActivity = ({ documents = [] }) => {
  const { theme } = useTheme();
  const dash = useDashboardUi(theme);
  
  // Fetch data from selected modules only: Documents, Complaints, Risk Assessment, Deviations, Approvals
  const { data: riskAssessments = [] } = useRiskAssessments();
  const { data: complaints = [] } = useComplaints();
  const { data: deviations = [] } = useDeviations();
  const { data: approvalRequests = [] } = useApprovalRequests();

  const recentActivities = useMemo(() => {
    const activities = [];
    
    // Helper function to create activity object
    const createActivity = (item, type, moduleName) => {
      const updatedDate = new Date(item.updatedAt);
      const createdDate = new Date(item.createdAt);
      const isNew = Math.abs(updatedDate - createdDate) < 60000; // Within 1 minute
      
      return {
        id: `${moduleName}-${item.id}`,
        type: isNew ? 'created' : 'updated',
        title: item.title || `${moduleName} ${item.id}`,
        code: item.code,
        user: item.updatedBy?.userDisplayName || item.createdBy?.userDisplayName || 'System',
        department: item.department?.name || 'Unknown',
        status: item.status?.name || 'Unknown',
        timestamp: updatedDate,
        timeAgo: getTimeAgo(updatedDate),
        module: moduleName,
        moduleIcon: getModuleIcon(moduleName),
        moduleSemantic: getModuleSemantic(moduleName),
      };
    };

    // Process selected modules only
    const allItems = [
      ...documents.map(doc => ({ ...doc, module: 'Document' })),
      ...riskAssessments.map(risk => ({ ...risk, module: 'Risk Assessment' })),
      ...complaints.map(complaint => ({ ...complaint, module: 'Complaint' })),
      ...deviations.map(deviation => ({ ...deviation, module: 'Deviation' })),
      ...approvalRequests.map(approval => ({ ...approval, module: 'Approval' }))
    ];

    // Sort by updatedAt and take the most recent 15
    allItems
      .filter(item => item.updatedAt)
      .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
      .slice(0, 15)
      .forEach(item => {
        activities.push(createActivity(item, 'updated', item.module));
      });

    return activities;
  }, [documents, riskAssessments, complaints, deviations, approvalRequests]);

  function getTimeAgo(date) {
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return date.toLocaleDateString();
  }

  function getModuleIcon(moduleName) {
    switch (moduleName) {
      case 'Document':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'CAPA':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        );
      case 'Risk Assessment':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case 'NCR':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'Change Request':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        );
      case 'Complaint':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
          </svg>
        );
      case 'Deviation':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case 'Approval':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'Training':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        );
      case 'Todo':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        );
      default:
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  }

  function getModuleSemantic(moduleName) {
    switch (moduleName) {
      case 'Document':
        return 'info';
      case 'Risk Assessment':
        return 'danger';
      case 'Complaint':
        return 'warning';
      case 'Deviation':
        return 'danger';
      case 'Approval':
        return 'success';
      default:
        return 'neutral';
    }
  }

  return (
    <div className={`${dash.widgetShell('h-full')}`}>
      <div className="flex items-center justify-between mb-6">
        <h3 className={`text-lg font-semibold ${theme.text.primary}`}>
          Recent Activity
        </h3>
        <button className={`text-sm ${dash.text('info')} hover:underline`}>
          View All
        </button>
      </div>

      {recentActivities.length === 0 ? (
        <div className="flex items-center justify-center h-48">
          <div className="text-center">
            <svg className={`w-12 h-12 mx-auto mb-4 ${theme.text.muted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className={`${theme.text.muted}`}>No recent activity</p>
          </div>
        </div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto advanced-table-scroll pr-1">
          {recentActivities.map((activity, index) => (
            <div
              key={activity.id + index}
              className={cn(
                'flex items-start gap-3 p-3 rounded-lg border transition-colors duration-200',
                dash.listItem(),
                theme.border.light,
              )}
            >
              <div className={cn('flex-shrink-0 mt-0.5', dash.iconText(activity.moduleSemantic))}>
                {activity.moduleIcon}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className={dash.badge(activity.moduleSemantic, 'text-xs')}>
                        {activity.module}
                      </span>
                      <span className={cn('text-xs', theme.text.muted)}>
                        {activity.type === 'created' ? 'Created' : 'Updated'}
                      </span>
                    </div>
                    <p className={cn('text-sm font-medium leading-snug', theme.text.primary)}>
                      <span className="line-clamp-2">
                        {activity.title}
                        {activity.code ? (
                          <span className={cn('ml-1.5 font-normal', theme.text.muted)}>
                            ({activity.code})
                          </span>
                        ) : null}
                      </span>
                    </p>
                    <p className={cn('text-xs mt-1 truncate', theme.text.secondary)}>
                      by {activity.user}
                      <span className={cn('mx-1', theme.text.muted)}>•</span>
                      {activity.department}
                    </p>
                  </div>
                  <div className="flex flex-col items-end shrink-0 gap-1">
                    <span className={cn('text-xs whitespace-nowrap tabular-nums', theme.text.muted)}>
                      {activity.timeAgo}
                    </span>
                    <span className={dash.badge(statusToSemantic(activity.status), 'text-xs whitespace-nowrap')}>
                      {activity.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View More */}
      {recentActivities.length >= 10 && (
        <div className={`mt-4 pt-4 border-t ${theme.border.light} text-center`}>
          <button className={`text-sm ${dash.text('info')} hover:underline`}>
            Load More Activities
          </button>
        </div>
      )}
    </div>
  );
};

export default RecentActivity;
